import { useEffect, useState } from 'react';

export interface SignVoucherImage {
  id: string;
  uri: string;
}

interface SignVoucherModalProps {
  isOpen: boolean;
  orderId: string;
  images: SignVoucherImage[];
  onClose: () => void;
}

export default function SignVoucherModal({
  isOpen,
  orderId,
  images,
  onClose,
}: SignVoucherModalProps) {
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setPreviewIndex(null);
      return;
    }
  }, [isOpen]);

  useEffect(() => {
    if (previewIndex === null) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setPreviewIndex(null);
        return;
      }
      if (e.key === 'ArrowLeft') {
        setPreviewIndex((idx) => {
          if (idx === null || images.length === 0) return idx;
          return (idx - 1 + images.length) % images.length;
        });
      }
      if (e.key === 'ArrowRight') {
        setPreviewIndex((idx) => {
          if (idx === null || images.length === 0) return idx;
          return (idx + 1) % images.length;
        });
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [previewIndex, images.length]);

  if (!isOpen) return null;

  const previewImage =
    previewIndex !== null && images[previewIndex] ? images[previewIndex] : null;

  return (
    <>
      <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/40" onClick={onClose}>
        <div
          className="bg-background-50 rounded-xl w-full max-w-lg mx-4 shadow-2xl max-h-[85vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-5 py-4 border-b border-background-200 flex items-center gap-3 shrink-0">
            <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 bg-primary-100">
              <i className="ri-image-line text-primary-600 text-lg" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-sm font-semibold text-foreground-900">签收凭证</h3>
              <p className="text-xs text-foreground-500 truncate mt-0.5">订单号 {orderId}</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 rounded-lg text-foreground-400 hover:text-foreground-700 hover:bg-background-100 flex items-center justify-center"
              aria-label="关闭"
            >
              <i className="ri-close-line text-lg" />
            </button>
          </div>

          <div className="px-5 py-4 overflow-y-auto">
            {images.length === 0 ? (
              <div className="py-12 text-center">
                <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-image-line text-2xl text-foreground-300" />
                </div>
                <p className="text-sm text-foreground-500">暂无签收凭证图片</p>
              </div>
            ) : (
              <>
                <p className="text-xs text-foreground-400 mb-3">共 {images.length} 张，点击图片可放大预览</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                  {images.map((img, index) => (
                    <button
                      type="button"
                      key={img.id}
                      onClick={() => setPreviewIndex(index)}
                      className="group relative aspect-square rounded-lg overflow-hidden border border-background-200 bg-background-100 focus:outline-none focus:ring-2 focus:ring-primary-400"
                    >
                      <img
                        src={img.uri}
                        alt={`签收凭证 ${index + 1}`}
                        className="w-full h-full object-cover transition-transform group-hover:scale-105"
                      />
                      <span className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                        <i className="ri-zoom-in-line text-background-50 text-xl opacity-0 group-hover:opacity-100 transition-opacity" />
                      </span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {previewImage && previewIndex !== null && (
        <div
          className="fixed inset-0 z-[120] bg-black/90 flex items-center justify-center"
          onClick={() => setPreviewIndex(null)}
        >
          <button
            type="button"
            onClick={() => setPreviewIndex(null)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
            aria-label="关闭预览"
          >
            <i className="ri-close-line text-xl" />
          </button>

          <div className="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-white/10 text-white text-xs">
            {previewIndex + 1} / {images.length}
          </div>

          {images.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setPreviewIndex((previewIndex - 1 + images.length) % images.length);
                }}
                className="absolute left-3 sm:left-6 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
                aria-label="上一张"
              >
                <i className="ri-arrow-left-s-line text-2xl" />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setPreviewIndex((previewIndex + 1) % images.length);
                }}
                className="absolute right-3 sm:right-6 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
                aria-label="下一张"
              >
                <i className="ri-arrow-right-s-line text-2xl" />
              </button>
            </>
          )}

          <img
            src={previewImage.uri}
            alt={`签收凭证预览 ${previewIndex + 1}`}
            className="max-w-[92vw] max-h-[88vh] object-contain rounded-md shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}
